import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, getRefreshToken, setTokens, clearAllAuth, readPersistedAuth } from '@/lib/tokenStorage';
import { resetIdentityScopedState } from '@/lib/sessionReset';

/**
 * Forcibly end the session and send the user to the login page. Used when the
 * session can't be recovered — either the account is no longer active
 * (deleted/deactivated by an admin) or token refresh failed. Clears all auth so
 * the post-redirect reload can't rehydrate a dead session. Guards against
 * redirect loops if we're already on the login page.
 */
function forceLogout(
  reason: 'account_inactive' | 'account_deactivated' | 'session_expired',
): void {
  clearAllAuth();
  // Auth material alone is not enough: the cached server data of the session
  // being ended would otherwise still be here for whoever signs in next.
  resetIdentityScopedState();
  const path = window.location.pathname;
  if (path === '/login' || path === '/platform-admin') return;
  window.location.href = `/login?reason=${reason}`;
}

/**
 * The account was deactivated by an admin, detected via the machine-readable
 * `ACCOUNT_DEACTIVATED` code the BFF returns (on 401 OR 403). Preferred over the
 * message heuristic below. The code can arrive top-level (`data.code`, from the
 * BFF AuthMiddleware) or nested (`data.error.code`, from backend services).
 */
function isAccountDeactivated(error: AxiosError): boolean {
  const data = error.response?.data as
    | { code?: string; error?: { code?: string } }
    | undefined;
  return (
    data?.code === 'ACCOUNT_DEACTIVATED' ||
    data?.error?.code === 'ACCOUNT_DEACTIVATED'
  );
}

/**
 * A 401 whose message means the account itself is gone/disabled (vs. a merely
 * expired access token). Refreshing can't recover these, so we log out at once.
 * Kept as a fallback for responses that lack the ACCOUNT_DEACTIVATED code.
 */
function isAccountInactive401(error: AxiosError): boolean {
  const data = error.response?.data as { message?: string } | undefined;
  const msg = (data?.message || '').toLowerCase();
  return /no longer active|not active|account.*(inactive|deactivat|deleted|removed|suspend)|session has been invalidated/.test(
    msg,
  );
}

// Use relative URL so Vite dev server can proxy requests to BFF
const BFF_URL = import.meta.env.VITE_BFF_URL || '/api';

// Default tenant ID for development (can be overridden by env variable)
const DEFAULT_TENANT_ID = import.meta.env.VITE_DEFAULT_TENANT_ID || 'default';

export const apiClient: AxiosInstance = axios.create({
  baseURL: BFF_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to get tenant ID from auth storage, tenant context store, or default
const getTenantId = (): string | null => {
  try {
    // First: try auth store (regular tenant users), persisted in localStorage
    // so it survives a tab close.
    const authStorage = readPersistedAuth();
    if (authStorage) {
      const parsed = JSON.parse(authStorage);
      if (parsed?.state?.user?.tenantId) {
        return parsed.state.user.tenantId;
      }
    }
    // Second: try tenant context store (platform admins managing a specific tenant)
    const tenantContext = localStorage.getItem('tenant-context-storage');
    if (tenantContext) {
      const parsed = JSON.parse(tenantContext);
      if (parsed?.state?.currentTenant?.id) {
        return parsed.state.currentTenant.id;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_TENANT_ID;
};

// Request interceptor to add auth token and tenant header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Fall back to platformAccessToken if regular accessToken not available
    const token = getAccessToken() || localStorage.getItem('platformAccessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add tenant header
    const tenantId = getTenantId();
    if (tenantId && config.headers) {
      config.headers['X-Tenant-Id'] = tenantId;
    }

    // Remove Content-Type header for FormData to let browser set it with correct boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Account deactivated by an admin → force logout regardless of status code
    // (the BFF returns 401 today, but honor 403 too). Token refresh can't
    // recover a deactivated account, so short-circuit before any refresh logic
    // and bounce the user to /login with the deactivation notice.
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      isAccountDeactivated(error)
    ) {
      forceLogout('account_deactivated');
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Account killed by an admin (deleted/deactivated) → refresh can't help.
      // Log the user out immediately and bounce them to the login page so they
      // can't keep using a dead session to view or change data.
      if (isAccountInactive401(error)) {
        forceLogout('account_inactive');
        return Promise.reject(error);
      }

      // Detect platform user: has platformAccessToken but no regular accessToken
      const isPlatformUser = !getAccessToken() && !!localStorage.getItem('platformAccessToken');

      if (isPlatformUser) {
        // Platform user — try platform token refresh
        const refreshToken = localStorage.getItem('platformRefreshToken');
        if (refreshToken) {
          try {
            const response = await axios.post(`${BFF_URL}/admin/auth/refresh`, { refreshToken });
            const { accessToken, refreshToken: newRefreshToken } = response.data;
            localStorage.setItem('platformAccessToken', accessToken);
            localStorage.setItem('platformRefreshToken', newRefreshToken);

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            }
            return apiClient(originalRequest);
          } catch {
            localStorage.removeItem('platformAccessToken');
            localStorage.removeItem('platformRefreshToken');
            window.location.href = '/platform-admin';
            return Promise.reject(error);
          }
        }
        window.location.href = '/platform-admin';
        return Promise.reject(error);
      }

      // Regular user — try regular token refresh
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post(`${BFF_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;
          setTokens(accessToken, newRefreshToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed — end the session and send them to login.
          forceLogout('session_expired');
          return Promise.reject(refreshError);
        }
      }

      // 401 with no refresh token available — can't recover; log out rather
      // than silently rejecting and leaving the user on a non-functional page.
      forceLogout('session_expired');
    }

    return Promise.reject(error);
  }
);

export default apiClient;
